import { useEffect, useRef, useState } from 'react'
import { apiGet, apiSend } from '../api/client'
import type { Envelope } from '../api/types'
import { FileDownloadDialog } from './FileDownloadDialog'
import { triggerDownload } from '../api/download'

export type DownloadState = {
  status: 'not_generated' | 'queued' | 'running' | 'ready' | 'failed'
  revision_id: string; file_name: string; page_count: number; source_count: number
  download_url: string | null; generated_at: string | null; file_size: number | null; error: string | null
}
const generating = (state?: string) => state === 'queued' || state === 'running'


/** GET 恢复基线级共享状态，POST 仅显式生成；文件只在用户点击下载时获取。 */
export default function BaselineDownload({ assetId, revisionId, assetName, label = '下载基线 PPT', lazy = false, onClose }: { assetId: string; revisionId: string | null; assetName?: string; label?: string; lazy?: boolean; onClose?: () => void }) {
  const [open, setOpen] = useState(false)
  const [download, setDownload] = useState<DownloadState | null>(null)
  const [error, setError] = useState('')
  const [checking, setChecking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [refresh, setRefresh] = useState(0)
  const lock = useRef(false), alive = useRef(true)
  const path = `/assets/${encodeURIComponent(assetId)}/revisions/${encodeURIComponent(revisionId ?? '')}/download`
  const state = download?.revision_id === revisionId ? download : null
  const close = () => { setOpen(false); onClose?.() }
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  useEffect(() => {
    if (!revisionId || lazy && !open) return
    let live = true, timer: ReturnType<typeof setTimeout> | undefined
    let reading = false
    const read = async () => {
      if (reading || !live) return
      reading = true; setChecking(true)
      if (timer) window.clearTimeout(timer)
      try {
        const response = await apiGet<Envelope<DownloadState>>(path)
        if (!live) return
        setDownload(response.data); setError('')
        if (generating(response.data.status) && document.visibilityState === 'visible') timer = window.setTimeout(() => void read(), 4000)
      } catch (caught) {
        if (live) setError(caught instanceof Error ? caught.message : String(caught))
      } finally { reading = false; if (live) setChecking(false) }
    }
    const resume = () => { if (document.visibilityState === 'visible') void read() }
    void read()
    document.addEventListener('visibilitychange', resume)
    return () => { live = false; if (timer) window.clearTimeout(timer); document.removeEventListener('visibilitychange', resume) }
  }, [path, revisionId, refresh, lazy, lazy ? open : null])

  const prepare = async () => {
    if (!revisionId || lock.current) return
    lock.current = true; setBusy(true); setError('')
    try {
      await apiSend('POST', path, {})
      if (alive.current) setRefresh(value => value + 1)
    } catch (caught) { if (alive.current) setError(caught instanceof Error ? caught.message : String(caught)) }
    finally { lock.current = false; if (alive.current) setBusy(false) }
  }
  const downloadFile = async () => {
    if (!revisionId || lock.current) return
    lock.current = true; setBusy(true); setError('')
    try {
      // 点击时重新检查授权、成品和短时签名，避免用户稍后返回时使用过期 URL。
      const response = await apiGet<Envelope<DownloadState>>(path)
      if (!alive.current) return
      const item = response.data
      setDownload(item)
      if (item.status !== 'ready' || !item.download_url) { setError('文件暂未就绪，请刷新状态后再试。'); return }
      triggerDownload(item.download_url, item.file_name)
    } catch (caught) { if (alive.current) setError(caught instanceof Error ? caught.message : String(caught)) }
    finally { lock.current = false; if (alive.current) setBusy(false) }
  }
  const inProgress = generating(state?.status)
  const ready = state?.status === 'ready'
  return <>
    <button type="button" className={`btn ghost baseline-download-trigger${ready ? ' is-ready' : ''}`} disabled={!revisionId} title={ready ? '成品已保存，点击查看并下载' : inProgress ? '后台生成中，可随时回来查看' : '查看基线 PPT 的生成和下载状态'} onClick={() => { setOpen(true); setRefresh(value => value + 1) }}>{inProgress ? 'PPT 生成中' : label}{ready ? <span className="baseline-ready-dot" aria-hidden="true" /> : null}</button>
    {open ? <FileDownloadDialog fileName={state?.file_name ?? `${assetName ?? '基线'}_${revisionId}.pptx`} version={revisionId ?? ''} pageCount={state?.page_count} status={state?.status ?? 'checking'} error={error || state?.error} busy={busy || checking} onClose={close} onRefresh={() => setRefresh(value => value + 1)} onGenerate={() => void prepare()} onDownload={() => void downloadFile()} /> : null}
  </>
}
